import {issueLinkedPullRequestHovercardPath} from '@github-ui/paths'

import {
  type BaseHovercardOptions,
  type HovercardAttributesResult,
  buildHovercardAttributes,
  useHovercardAttributesBase,
} from './shared'

export type PullRequestHovercardOptions = BaseHovercardOptions &
  (
    | {
        owner: string | undefined
        repo: string | undefined
        pullRequestNumber: number | undefined
        url?: never
      }
    | {
        /** URL override - use when the API provides a pre-computed hovercard URL */
        url: string
        owner?: never
        repo?: never
        pullRequestNumber?: never
      }
  )

/**
 * Generate hovercard attributes for a pull request.
 */
function pullRequestHovercardAttributes(options: PullRequestHovercardOptions): HovercardAttributesResult {
  let url: string

  if ('url' in options && options.url) {
    url = options.url
  } else if ('owner' in options && options.owner && options.repo && options.pullRequestNumber != null) {
    url = issueLinkedPullRequestHovercardPath({
      owner: options.owner,
      repo: options.repo,
      pullRequestNumber: options.pullRequestNumber,
    })
  } else {
    return {}
  }

  return buildHovercardAttributes(url, 'pull_request', options.tracking ?? true)
}

/**
 * React hook for pull request hovercard attributes.
 *
 * @example
 * ```tsx
 * function PRLink({ owner, repo, pullRequestNumber }: Props) {
 *   const hovercardAttrs = useHovercardAttributesForPullRequest({owner, repo, pullRequestNumber})
 *   return <a href={`/${owner}/${repo}/pull/${pullRequestNumber}`} {...hovercardAttrs}>#{pullRequestNumber}</a>
 * }
 * ```
 */
export function useHovercardAttributesForPullRequest(options: PullRequestHovercardOptions): HovercardAttributesResult {
  return useHovercardAttributesBase(pullRequestHovercardAttributes(options))
}
